/**
 * The ONE drag controller behind every manual queue (F-25 and its twins).
 *
 * Three views drop rows with it — «Потоки» (stream positions), «Команды»
 * (team roster) and «Исполнители» (a person's own queue) — so the drag state
 * machine lives here instead of being copied three times. The wire operation
 * is always the same shape: the view owns the FULL id order and receives the
 * reordered one through `onApply` (`null` = nothing to do — same row, an id
 * outside the list, an edge nudge).
 *
 * `manualRowDragProps` is the row-level HTML5 half: the WHOLE `<tr>` is
 * draggable (owner feedback #1 — dragging the row itself used to start a text
 * drag with a blocked cursor), while a drag started inside an interactive
 * control (button / select / input / link) belongs to that control and never
 * to the row. Passing the row's render payload also mounts a compact ghost
 * card that rides below-right of the pointer for the whole drag — the owner
 * must see WHICH project is under the cursor and where it lands.
 */

import { useCallback, useState } from 'react';
import type { DragEvent } from 'react';

import { nudgeId, reorderIds } from '../lib/manual-order';

/** Consumed by the row renderer (ProjectRow, the queue tables). */
export interface ManualOrderControls {
  /** `false` outside the «Вручную» sort mode — the handle renders disabled. */
  enabled: boolean;
  busy: boolean;
  /** The full current id order of the list (display order). */
  order: readonly string[];
  /** The row currently dragged (null = no drag in progress). */
  dragId: string | null;
  /** The row the drag currently hovers over (drop-target highlight). */
  overId: string | null;
  onDragStart: (id: string) => void;
  onDragOverRow: (id: string) => void;
  onDropRow: (targetId: string) => void;
  onDragEnd: () => void;
  /** Keyboard alternative to the drag (↑/↓ next to the handle). */
  onNudge: (id: string, delta: -1 | 1) => void;
}

export interface UseManualOrderOptions {
  enabled: boolean;
  busy: boolean;
  order: readonly string[];
  /** Commits the reordered list; `null` outcomes are ignored by the view. */
  onApply: (nextIds: readonly string[] | null) => void;
}

/** One drag at a time per list — exactly the old app's `{dragId, overId}`. */
export function useManualOrder({ enabled, busy, order, onApply }: UseManualOrderOptions): ManualOrderControls {
  const [drag, setDrag] = useState<{ dragId: string | null; overId: string | null }>({ dragId: null, overId: null });
  const reset = useCallback(() => setDrag({ dragId: null, overId: null }), []);

  return {
    enabled,
    busy,
    order,
    dragId: drag.dragId,
    overId: drag.overId,
    onDragStart: (id) => setDrag({ dragId: id, overId: id }),
    onDragOverRow: (id) => setDrag((current) => (current.dragId === null || current.overId === id ? current : { ...current, overId: id })),
    onDropRow: (targetId) => {
      const dragId = drag.dragId;
      reset();
      if (dragId === null) return;
      onApply(reorderIds(order, dragId, targetId));
    },
    onDragEnd: reset,
    onNudge: (id, delta) => onApply(nudgeId(order, id, delta)),
  };
}

/** A drag/click started inside a control belongs to that control, never to the
 *  row. Shared by the drag guard here and by the row-click-to-edit handler of
 *  the queue tables («Исполнители», «Команды»). */
export const INTERACTIVE = 'button, select, input, textarea, a';

/**
 * What the drag ghost card shows (owner feedback: «project card must be below
 * the mouse pointer so it's clear at which position it lands»). Labels are
 * passed in ready-made — the hook stays free of the dictionaries.
 */
export interface DragGhostPayload {
  /** Project title, one line, bold. */
  title: string;
  /** Localised status label (the pill). */
  statusLabel: string;
  /** Localised priority label. */
  priorityLabel: string;
}

/** The single ghost node of a drag (one drag at a time per browser anyway). */
const DRAG_GHOST_ID = 'plans-drag-ghost';

/** Builds the offscreen ghost card; `null` outside a DOM (SSR / unit tests). */
function mountDragGhost(payload: DragGhostPayload): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const ghost = document.createElement('div');
  ghost.id = DRAG_GHOST_ID;
  ghost.className = 'drag-ghost';
  const title = document.createElement('span');
  title.className = 'drag-ghost__title';
  title.textContent = payload.title;
  const meta = document.createElement('span');
  meta.className = 'drag-ghost__meta';
  const pill = document.createElement('span');
  pill.className = 'drag-ghost__pill';
  pill.textContent = payload.statusLabel;
  const priority = document.createElement('span');
  priority.className = 'drag-ghost__label';
  priority.textContent = payload.priorityLabel;
  meta.append(pill, priority);
  ghost.append(title, meta);
  document.body.appendChild(ghost);
  return ghost;
}

/** `dragend` guard: the node must never outlive its drag. */
function removeDragGhost(): void {
  document.getElementById(DRAG_GHOST_ID)?.remove();
}

/** Row-level HTML5 drag props for ONE row of a manual-order table.
 *
 * `ghost` is optional: pass the row's render payload to get the compact card
 * under the pointer for the whole drag (see `DragGhostPayload`). */
export function manualRowDragProps(
  id: string,
  controls: ManualOrderControls | undefined,
  ghost?: DragGhostPayload,
): {
  draggable: boolean;
  onDragStart: ((event: DragEvent<HTMLElement>) => void) | undefined;
  onDragOver: ((event: DragEvent<HTMLElement>) => void) | undefined;
  onDrop: ((event: DragEvent<HTMLElement>) => void) | undefined;
  onDragEnd: (() => void) | undefined;
} {
  const enabled = controls !== undefined && controls.enabled;
  return {
    draggable: enabled,
    onDragStart: enabled
      ? (event) => {
          if ((event.target as HTMLElement | null)?.closest(INTERACTIVE)) return;
          event.dataTransfer.effectAllowed = 'move';
          // Firefox starts no drag without payload data.
          event.dataTransfer.setData('text/plain', id);
          if (ghost !== undefined) {
            const element = mountDragGhost(ghost);
            if (element !== null) {
              try {
                // 16/20 = below-right of the pointer, so the pointer sits on
                // the card's top-left corner and the landing row stays visible.
                event.dataTransfer.setDragImage(element, 16, 20);
              } catch {
                // Engines without setDragImage (jsdom): the default image stays.
              }
              // The bitmap is captured synchronously — the node can go on the
              // very next tick (it stays out of the layout at -9999px anyway).
              window.setTimeout(() => element.remove(), 0);
            }
          }
          controls?.onDragStart(id);
        }
      : undefined,
    onDragOver: enabled
      ? (event) => {
          if (controls?.dragId === null || controls === undefined) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
          controls.onDragOverRow(id);
        }
      : undefined,
    onDrop: enabled
      ? (event) => {
          event.preventDefault();
          controls?.onDropRow(id);
        }
      : undefined,
    onDragEnd: enabled
      ? () => {
          // The timer above normally removed the ghost already; a cancelled or
          // immediate dragend must not leak the node regardless — the state
          // reset runs whether or not the removal throws.
          try {
            removeDragGhost();
          } finally {
            controls?.onDragEnd();
          }
        }
      : undefined,
  };
}
